import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  PropsWithChildren,
} from "react";
import { useQueryState } from "nuqs";
import _uniq from "lodash/uniq";

import { useThrottledValue } from "shared/lib/hooks/use-throttled-value";
import type { ChatItemI, ChatContactI } from "shared/ui/messages/types";

import { useUser } from "@/hooks/use-global-context";

import {
  getCampaignIdFromChatIdentity,
  getChatIdentity,
  getCustomerIdFromChatIdentity,
} from "@/modules/messages/utils";

import {
  useQueryFetchContactsToMessage,
  useQueryFetchChats,
  useQueryFetchMessages,
  useQueryFetchOnlineStatuses,
} from "./data/queries";
import {
  useMutationResetUnreadMessagesCount,
  useMutationSendMessage,
} from "./data/mutations";
import { useHandleChatWebSocket } from "@/modules/messages/hooks";
import { CampaignsByIdMap, useCampaignsMapById } from "@/hooks/use-campaigns";

export const CHAT_ID_STORAGE_KEY = "selectedChatId";

interface MessagingContextI {
  contactsQuery: ReturnType<typeof useQueryFetchContactsToMessage>;
  onlineStatusesQuery: ReturnType<typeof useQueryFetchOnlineStatuses>;
  chatsQuery: ReturnType<typeof useQueryFetchChats>;
  messagesQuery: ReturnType<typeof useQueryFetchMessages>;
  campaignsByIdMap: CampaignsByIdMap;

  chatsSearchTerm: string;
  setChatsSearchTerm: (term: string) => void;

  sendMessageMutation: ReturnType<typeof useMutationSendMessage>;
  resetUnreadMessagesCountMutation: ReturnType<
    typeof useMutationResetUnreadMessagesCount
  >;

  hasChatHistory: boolean;
  hasContactToChat: boolean | undefined;

  selectedChatId: string | null;
  setSelectedChatId: (chatId: string | null) => void;
  selectedChat: ChatItemI | undefined;
  selectedContact: ChatContactI | undefined;
  selectedContactRole?: string | undefined;
  currentUser: ChatContactI;
}

interface MessagingContextProviderPropsI extends PropsWithChildren {
  campaignId?: string;
  campaignName?: string;
  contactId?: string;
}

const MessagingContext = createContext<MessagingContextI | undefined>(
  undefined
);

export const MessagingContextProvider = ({
  campaignId: externalCampaignId,
  campaignName: externalCampaignName,
  contactId: externalContactId,
  children,
}: MessagingContextProviderPropsI) => {
  const me = useUser();
  const campaignsByIdMap = useCampaignsMapById();

  const [chatIdFromUrl, setChatIdInUrl] = useQueryState("chatId");
  const [chatIdFromStorage, setChatIdInStorage] = useState<string | null>(
    localStorage.getItem(CHAT_ID_STORAGE_KEY)
  );

  const currentUser = useMemo(
    () => ({
      id: me?.id as string,
      first_name: me?.first_name as string,
      last_name: me?.last_name as string,
      profile_pic_url: me?.profile_image_url,
      isOnline: true,
    }),
    [me]
  );

  const [chatsSearchTerm, setChatsSearchTerm] = useState("");
  const throttledChatsSearchTerm = useThrottledValue(chatsSearchTerm);

  const chatsQuery = useQueryFetchChats(throttledChatsSearchTerm);

  const contactsQuery = useQueryFetchContactsToMessage();

  const onlineStatusesQuery = useQueryFetchOnlineStatuses(
    externalContactId
      ? [externalContactId]
      : _uniq(
          contactsQuery.data?.flatMap((campaignCustomer) =>
            campaignCustomer.customers.map((customer) => customer.id)
          ) || []
        )
  );

  const selectedChatId = useMemo(() => {
    /**
     * If rendered for a specific campaign (i.e., for the 'account details mode'),
     * show chat with a default contact customer
     */
    if (externalCampaignId && externalContactId) {
      return getChatIdentity({
        campaign_id: externalCampaignId,
        customer: {
          id: externalContactId,
        },
      });
    }

    return chatIdFromUrl || chatIdFromStorage;
  }, [externalCampaignId, externalContactId, chatIdFromUrl, chatIdFromStorage]);

  const setSelectedChatId = (chatId: string | null) => {
    if (chatId === selectedChatId) {
      return;
    }

    void setChatIdInUrl(chatId);

    if (chatId) {
      localStorage.setItem(CHAT_ID_STORAGE_KEY, chatId);
      setChatIdInStorage(chatId);
    } else {
      localStorage.removeItem(CHAT_ID_STORAGE_KEY);
      setChatIdInStorage(null);
    }
  };

  const selectedChat = useMemo(() => {
    if (!selectedChatId || chatsQuery.data === undefined) {
      return undefined;
    }

    const chatFromQuery = chatsQuery.data.find(
      (chat) => selectedChatId === getChatIdentity(chat)
    ) as ChatItemI | undefined;

    if (selectedChatId && !chatFromQuery) {
      const contactByChatId = contactsQuery.data
        ?.map(({ customers }) => customers)
        ?.flat()
        ?.find(
          (customer) =>
            customer.id === getCustomerIdFromChatIdentity(selectedChatId)
        );

      if (!contactByChatId) {
        return undefined;
      }

      return {
        campaign_id: getCampaignIdFromChatIdentity(selectedChatId),
        user: currentUser,
        customer: contactByChatId,
        last_message: "",
        last_message_timestamp: "",
        notification_count: 0,
      };
    }

    return chatFromQuery;
  }, [chatsQuery.data, selectedChatId, currentUser]);

  const campaignId = externalCampaignId || selectedChat?.campaign_id;

  const selectedContact = selectedChat?.customer;

  const selectedContactRole = useMemo(() => {
    if (externalCampaignName) {
      return `AE at ${externalCampaignName}`;
    }

    if (!campaignsByIdMap || !selectedChat?.campaign_id) {
      return undefined;
    }

    const customerCampaignName =
      campaignsByIdMap[selectedChat.campaign_id]?.name;

    if (!customerCampaignName) {
      return undefined;
    }

    return `AE at ${customerCampaignName}`;
  }, [externalCampaignName, campaignsByIdMap, selectedChat?.campaign_id]);

  const messagesQuery = useQueryFetchMessages(campaignId, selectedContact?.id);

  const sendMessageMutation = useMutationSendMessage(
    campaignId,
    selectedContact?.id
  );
  const resetUnreadMessagesCountMutation =
    useMutationResetUnreadMessagesCount();

  const hasChatHistory = Boolean(
    chatsQuery.data && chatsQuery.data.length > 0 && !chatsQuery.isFetching
  );

  const hasContactToChat = useMemo(() => {
    if (!contactsQuery.data) {
      return undefined;
    }

    return Boolean(contactsQuery.data && selectedContact);
  }, [contactsQuery.data, selectedContact]);

  /**
   * Handle incoming WebSocket messages, which include:
   * 1) Entering a chat room
   * 2) Receiving a new message
   */
  useHandleChatWebSocket(campaignId, currentUser, selectedContact);

  useEffect(() => {
    if (chatIdFromUrl && chatIdFromUrl !== chatIdFromStorage) {
      localStorage.setItem(CHAT_ID_STORAGE_KEY, chatIdFromUrl);
      setChatIdInStorage(chatIdFromUrl);
    }
  }, [chatIdFromUrl, chatIdFromStorage]);

  return (
    <MessagingContext.Provider
      value={{
        contactsQuery,
        onlineStatusesQuery,
        chatsQuery,
        messagesQuery,

        sendMessageMutation,
        resetUnreadMessagesCountMutation,

        campaignsByIdMap,

        hasChatHistory,
        hasContactToChat,

        selectedChatId,
        setSelectedChatId,
        selectedChat,
        selectedContact,
        selectedContactRole,
        currentUser,
        chatsSearchTerm,
        setChatsSearchTerm,
      }}
    >
      {children}
    </MessagingContext.Provider>
  );
};

export const useMessagingContext = () => {
  const context = useContext(MessagingContext);

  if (context === undefined) {
    throw new Error(
      "useMessagingContext must be used within a MessagingContextProvider"
    );
  }

  return context;
};
